import * as React from "react"
import { Check, ChevronsUpDown, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"

interface ComboboxItem {
  label: string
  value: string
  className?: string
  style?: React.CSSProperties
}

interface ComboboxProps {
  items: ComboboxItem[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  /** Styling for the dropdown itself — e.g. zIndex when used inside modals. */
  contentClassName?: string
  contentStyle?: React.CSSProperties
}

export function Combobox({ items, value, onChange, placeholder = "Select...", className, contentClassName, contentStyle }: ComboboxProps) {
  const [searchQuery, setSearchQuery] = React.useState("")
  const [open, setOpen] = React.useState(false)

  const filteredItems = React.useMemo(() => {
    if (!searchQuery) return items
    return items.filter((item) =>
      item.label.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [items, searchQuery])

  return (
    <Select 
      value={value} 
      onValueChange={onChange}
      open={open}
      onOpenChange={setOpen}
    >
      <SelectTrigger className={cn("w-full h-7 px-2 text-sm border-[0.5px] rounded-md", className)}>
        <SelectValue placeholder={placeholder}>
          {items.find(item => item.value === value)?.label || placeholder}
        </SelectValue>
      </SelectTrigger>
      <SelectContent
        className={cn(
          "bg-white dark:bg-[#182438] border-[0.5px] border-neutral-200 dark:border-[#333840] rounded-md p-1",
          contentClassName
        )}
        style={contentStyle}
      >
        <div className="flex items-center px-2 pb-1">
          <Search className="mr-1 h-3 w-3 shrink-0 opacity-50" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-6 w-full bg-transparent border-none focus:ring-0 focus:border-none p-0 text-sm placeholder:text-muted-foreground"
          />
        </div>
        <div className="max-h-[250px] overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="relative flex w-full select-none items-center rounded-sm py-1 px-2 text-xs outline-none text-muted-foreground">
              No results found
            </div>
          ) : (
            filteredItems.map((item) => (
              <SelectItem
                key={item.value}
                value={item.value}
                className={cn(
                  "cursor-pointer h-7 text-sm px-2 data-[highlighted]:bg-emerald-50/50 data-[highlighted]:text-emerald-900 dark:data-[highlighted]:bg-emerald-950/30 dark:data-[highlighted]:text-emerald-50",
                  item.className
                )}
                style={item.style}
              >
                <span className="truncate">{item.label}</span>
              </SelectItem>
            ))
          )}
        </div>
      </SelectContent>
    </Select>
  )
}
